// @flow

import * as React from 'react';

import clsx from 'clsx';

import { useTranslator } from '@gandi/translate';

import { useDisclosure } from '@gandi/ui';

import styles from './Collapsible.scss';

type ActionProps = {
  icon: string,
  children: string,
  ...
};

type MergedProps = {
  className?: string,
  onClick?: (evt: SyntheticMouseEvent<>) => mixed,
  ...
};

export type Props = {
  id?: string,
  collapsed?: boolean,
  render: (
    getActionProps: (props?: MergedProps) => ActionProps,
    getDrawerProps: (props?: MergedProps) => { ... },
    isCollapsed: boolean,
  ) => React.MixedElement,
};

/**
 * /!\ Temporary proxy component to ease the gandi/ui 17 migration.
 *
 * Do NOT use in new components,
 * use `Disclosure` or `useDisclosure` instead !
 */
function Collapsible({
  id,
  collapsed = false,
  render,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const { open, triggerProps, contentProps } = useDisclosure({
    id,
    defaultOpen: !collapsed,
  });

  const getActionProps = ({
    className,
    onClick,
    ...rest
  }: MergedProps = {}) => {
    const handleClick = (evt: SyntheticMouseEvent<>) => {
      triggerProps.onClick();

      onClick?.(evt);
    };

    // $FlowFixMe[cannot-spread-inexact]
    return {
      ...triggerProps,
      type: 'button',
      icon: open ? 'miniChevronTop' : 'miniChevronBottom',
      children: open ? __('Collapse') : __('Expand'),
      className: clsx(styles.button, className),
      onClick: handleClick,
      ...rest,
    };
  };

  // $FlowFixMe[cannot-spread-inexact]
  const getDrawerProps = (props: { ... } = {}) => ({
    ...contentProps,
    ...props,
  });

  return render(getActionProps, getDrawerProps, !open);
}

export default Collapsible;
